import { useHistory } from 'react-router';
import {
  useExistingAppointments,
  useMutateCallAppointment,
} from '../../../query/appointments';
import { Appointment } from '../../../types/appointments';
import { CubicleData } from '../../../types/cubicles';
import { Refetch } from '../../../types/query';
import {
  MILLIS_IN_SECOND,
  todayInIso,
  tomorrowInIso,
} from '../../../utils/dateUtil';
import { queuedProcessingApptRoute } from '../../../routes/staff/StaffRoutesAndMenu';
import { UseMutateFunction } from 'react-query';
import { useCommonTranslation } from '../../../hooks/i18n/useCommonTranslation';
import { useUserDisabilities } from '../../../hooks/user/useUserDisabilities';
import { useParseApptFlags } from '../../../hooks/appointments/useParseApptFlags';

export const useQueuedAppointments = ({
  cubicle,
}: {
  cubicle?: CubicleData;
}): {
  isLoading: boolean;
  appointments: Appointment[] | null | undefined;
  refetch: Refetch;
  callAppointment: UseMutateFunction<unknown, unknown, string, unknown>;
} => {
  const today = todayInIso();
  const tomorrow = tomorrowInIso();
  const {
    data,
    isLoading: existingAppointmentsLoading,
    refetch,
  } = useExistingAppointments(
    {
      statuses: ['QUEUE'],
      fromDate: today,
      toDate: tomorrow,
    },
    {
      refetchInterval: 5 * MILLIS_IN_SECOND,
      enabled: !!cubicle,
    },
  );

  const { replace } = useHistory();

  const { isLoading: callAppointmentLoading, mutate: callAppointment } =
    useMutateCallAppointment({
      /**
       * Needs to navigate after the cache has been changed and correct queries invalidated.
       */
      successAfterSettled: (apptId) =>
        replace(queuedProcessingApptRoute(apptId)),
    });

  return {
    isLoading: existingAppointmentsLoading || callAppointmentLoading,
    /**
     * Optimistic cache only update ignore filters, so this is useful for that case
     */
    appointments:
      data?.data.data?.filter(({ status }) => {
        return status === 'QUEUE';
      }) || [],
    refetch,
    callAppointment,
  };
};

export type ApptPatientInfoInput = Pick<
  Appointment,
  'appt_flags' | 'user_disabilities'
>;

export const useQueuedAppointmentPatientInfo = ({
  appt_flags,
  user_disabilities,
}: ApptPatientInfoInput = {}) => {
  const { t } = useCommonTranslation();
  const userDisabilities = useUserDisabilities(user_disabilities);
  const apptFlags = useParseApptFlags(appt_flags);

  const infoText = [];
  infoText.push(...apptFlags.map((flag) => flag.label));
  userDisabilities.forEach((disability) => {
    switch (disability) {
      case 'Movement':
        infoText.push(t('disability-movement'));
        break;
      case 'Hearing':
        infoText.push(t('disability-hearing'));
        break;
      case 'Sight':
        infoText.push(t('disability-sight'));
        break;
    }
  });
  return {
    hasInfo: infoText.length > 0,
    textValue: infoText.join(', ').toUpperCase(),
  };
};
